import { useStore } from "vuex";
import { onMounted, onBeforeUnmount, ref, computed, watch } from "vue";
import { columns, options } from "@/helpers/devicesTabTableHelper";

export default function useDevicesTable(courseId) {
  const store = useStore();

  const isPageChangeEnabled = ref(true);
  const selected_rows = ref([]);
  const searchTerm = ref("");
  const sortOrder = ref({ key: "id", direction: "asc" });

  const currentPage = ref(store.getters["devices/getCurrentPage"]);
  const pageSize = ref(store.getters["devices/getPerPage"]);
  const totalRecords = ref(computed(() => store.getters["devices/getTotal"]));
  const tableData = ref(
    computed(() => store.getters["devices/getCourseDevices"])
  );
  const isDeleted = ref(computed(() => store.getters["devices/getIsDeleted"]));
  const course = ref(computed(() => store.getters["course/getCourse"]));

  watch(isDeleted, (newValue) => {
    if (newValue === true) {
      fetchData().then().catch(console.error);
      store.dispatch("devices/clearIsDeleted");
    }
  });

  const isLoading = computed(() => store.getters["devices/isLoading"]);

  const fetchData = async () => {
    const payload = {
      courseId: courseId,
      page: currentPage.value,
      pageSize: pageSize.value,
      searchTerm: searchTerm.value,
      sortOrder: sortOrder.value,
    };
    store.dispatch("devices/fetchCourseDevices", payload);
  };

  const tableOptions = {
    ...options,
    pageLength: pageSize.value,
    deferLoading: totalRecords.value,
    ajax: (data, callback) => {
      currentPage.value = data.start / data.length + 1;
      !isLoading.value &&
        callback({
          draw: data.draw,
          recordsTotal: totalRecords.value,
          recordsFiltered: totalRecords.value,
          data: tableData.value,
        });
    },
  };

  onMounted(async () => {
    document.addEventListener("click", handleClick);
    fetchData();
  });
  onBeforeUnmount(() => {
    // Reset the current page and search term when the component is unmounted
    store.dispatch("devices/clearCourseDevices");
    // Clean up event listener to prevent memory leaks
    document.removeEventListener("click", handleClick);
  });

  const handleClick = (event) => {
    if (event.target && event.target.classList.contains("view-device-btn")) {
      const deviceData = event.target.getAttribute("data");
      const device = JSON.parse(deviceData.replace(/&quot;/g, '"'));
      viewDeviceLogs(device);
    }
  };

  const onSelect = ({ dt, type }) => {
    if (type === "row") {
      selected_rows.value = dt.rows({ selected: true }).data().toArray();
    }
  };
  const onDeselect = ({ dt, type }) => {
    if (type === "row") {
      selected_rows.value = dt.rows({ selected: true }).data().toArray();
    }
  };

  const handleLengthChange = ({ dt }) => {
    const newLength = dt._iDisplayLength;
    currentPage.value = 1;
    pageSize.value = newLength;
    fetchData();
    isPageChangeEnabled.value = false;
    dt.api.page(currentPage.value - 1).draw(false);
    isPageChangeEnabled.value = true;
  };

  const onPageChange = ({ event }) => {
    if (!isPageChangeEnabled.value) return;
    const info = event.dt.page.info();
    currentPage.value = info.page + 1;
    fetchData();
  };

  const handleSearch = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.search === "function") {
      const searchValue = dataTable.search();
      if (searchTerm.value !== searchValue) {
        searchTerm.value = searchValue;
        currentPage.value = 1;
        fetchData();
        isPageChangeEnabled.value = false;
        dt.api.page(currentPage.value - 1).draw(false);
        isPageChangeEnabled.value = true;
      }
    }
  };

  const sortKeyValuePair = (columnIdx, direction) => {
    if (!direction) return {};
    else if (columnIdx === 1) return { key: "tag", direction };
    else if (columnIdx === 2) return { key: "imei", direction };
    else if (columnIdx === 3) return { key: "iccid", direction };
    else if (columnIdx === 4) return { key: "class", direction };
    else if (columnIdx === 5) return { key: "type", direction };
    else if (columnIdx === 6) return { key: "last_fix", direction };
    else if (columnIdx === 7) return { key: "build", direction };
    return {};
  };

  const handleSortChange = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.order === "function") {
      const order = dataTable.order();
      if (!order || order.length === 0) {
        return;
      }

      const columnIdx = order[0][0];
      const direction = order[0][1];
      const sortValue = sortKeyValuePair(columnIdx, direction);

      if (
        JSON.stringify(sortOrder.value) !== JSON.stringify(sortValue) &&
        Object.keys(sortValue).length > 0
      ) {
        sortOrder.value = sortValue;
        currentPage.value = 1;
        fetchData();
        isPageChangeEnabled.value = false;
        dt.api.page(currentPage.value - 1).draw(false);
        isPageChangeEnabled.value = true;
      }
    }
  };

  const viewDeviceLogs = (device) => {
    const payload = { imei: device.imei };
    store.dispatch("devices/fetchDeviceLogs", payload);
    //router.push({ name: "device", params: { id: device.id } });
  };

  const deleteDevices = async () => {
    const ids = selected_rows.value.map((row) => row.id);
    const imeis = selected_rows.value.map((row) => row.imei);

    const payload = {
      ids: ids,
      imeis: imeis,
      courses: [course.value.url],
    };
    store.dispatch("devices/deleteDevices", payload);
  };

  const sendLogsCommand = async () => {
    const ids = selected_rows.value.map((row) => row.id);
    const payload = {
      ids: ids,
      courseId: courseId,
      actionType: "logs",
    };
    await store.dispatch("devices/sendCommand", payload);
  };

  const sendConfigCommand = async () => {
    const ids = selected_rows.value.map((row) => row.id);
    const payload = {
      ids: ids,
      courseId: courseId,
      actionType: "config",
    };
    await store.dispatch("devices/sendCommand", payload);
  };

  const sendCoursesCommand = async () => {
    const ids = selected_rows.value.map((row) => row.id);
    const payload = {
      ids: ids,
      courseId: courseId,
      actionType: "courses",
    };
    await store.dispatch("devices/sendCommand", payload);
  };

  const sendAdvertisementsCommand = async () => {
    const ids = selected_rows.value.map((row) => row.id);
    const payload = {
      ids: ids,
      courseId: courseId,
      actionType: "advertisments",
    };
    await store.dispatch("devices/sendCommand", payload);
  };

  return {
    columns,
    tableData,
    tableOptions,
    handleSearch,
    handleSortChange,
    onSelect,
    onDeselect,
    handleLengthChange,
    onPageChange,
    deleteDevices,
    sendLogsCommand,
    sendConfigCommand,
    sendCoursesCommand,
    sendAdvertisementsCommand,
  };
}
